import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Calendar,
  Plus,
  Check,
  X,
  Clock,
  Umbrella,
  ThermometerSun,
  Coffee,
  Edit2,
  Save,
  AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIME_OFF_TYPES = ['Vacation', 'Sick', 'Personal', 'FMLA', 'Bereavement', 'Jury Duty', 'Unpaid'];

export default function PersonnelPTO({ personnelId, isAdmin = true }) {
  const [ptoBalance, setPtoBalance] = useState(null);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [editingBalance, setEditingBalance] = useState(false);
  const [newRequest, setNewRequest] = useState({
    start_date: '',
    end_date: '',
    type: 'Vacation',
    days_requested: 1,
    notes: ''
  });
  const [editedBalance, setEditedBalance] = useState({});
  const [visitContext, setVisitContext] = useState(null);
  const [checkingVisits, setCheckingVisits] = useState(false);

  useEffect(() => {
    fetchPTOData();
  }, [personnelId]);

  const fetchPTOData = async () => {
    setLoading(true);
    try {
      const [balanceRes, personRes] = await Promise.all([
        axios.get(`${API}/personnel/${personnelId}/pto-balance`),
        axios.get(`${API}/personnel/${personnelId}`)
      ]);
      setPtoBalance(balanceRes.data);
      setTimeOffRequests(personRes.data.time_off_requests || []);
      setEditedBalance(balanceRes.data);
    } catch (error) {
      console.error('Failed to load PTO data:', error);
      toast.error('Failed to load PTO data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!newRequest.start_date || !newRequest.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    try {
      await axios.post(`${API}/personnel/${personnelId}/time-off`, newRequest);
      toast.success('Time off request submitted');
      setShowNewRequest(false);
      setVisitContext(null);
      setNewRequest({ start_date: '', end_date: '', type: 'Vacation', days_requested: 1, notes: '' });
      fetchPTOData();
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  const checkVisitsDuringPeriod = async () => {
    if (!newRequest.start_date || !newRequest.end_date) {
      toast.error('Select dates first');
      return;
    }
    setCheckingVisits(true);
    try {
      const res = await axios.post(`${API}/personnel/${personnelId}/time-off-notify`, {
        start_date: newRequest.start_date,
        end_date: newRequest.end_date,
        type: newRequest.type,
        days_requested: newRequest.days_requested,
      });
      setVisitContext(res.data);
    } catch (e) {
      console.error('Failed to check visits:', e);
      setVisitContext(null);
    }
    finally { setCheckingVisits(false); }
  };

  const handleUpdateRequest = async (requestId, status) => {
    try {
      await axios.put(`${API}/personnel/${personnelId}/time-off/${requestId}`, { status });
      toast.success(`Request ${status.toLowerCase()}`);
      fetchPTOData();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleSaveBalance = async () => {
    try {
      await axios.put(`${API}/personnel/${personnelId}/pto-balance`, {
        vacation_balance: parseFloat(editedBalance.vacation_balance) || 0,
        sick_balance: parseFloat(editedBalance.sick_balance) || 0,
        personal_balance: parseFloat(editedBalance.personal_balance) || 0,
        vacation_accrual_rate: parseFloat(editedBalance.vacation_accrual_rate) || 0,
        sick_accrual_rate: parseFloat(editedBalance.sick_accrual_rate) || 0,
        personal_accrual_rate: parseFloat(editedBalance.personal_accrual_rate) || 0,
      });
      toast.success('PTO balance updated');
      setEditingBalance(false);
      fetchPTOData();
    } catch (error) {
      toast.error('Failed to update balance');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-green-100 text-green-800',
      'Denied': 'bg-red-100 text-red-800',
      'Cancelled': 'bg-gray-100 text-gray-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Vacation': return <Umbrella size={14} />;
      case 'Sick': return <ThermometerSun size={14} />;
      case 'Personal': return <Coffee size={14} />;
      default: return <Calendar size={14} />;
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground">Loading PTO data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* PTO Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Umbrella className="text-blue-600" size={20} />
              <span className="font-medium">Vacation</span>
            </div>
            {editingBalance ? (
              <Input
                type="number"
                step="0.5"
                className="w-20 h-8 text-right"
                value={editedBalance.vacation_balance || 0}
                onChange={(e) => setEditedBalance({ ...editedBalance, vacation_balance: e.target.value })}
              />
            ) : (
              <span className="text-2xl font-bold text-blue-600">
                {ptoBalance?.available_vacation?.toFixed(1) || '0.0'}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {ptoBalance?.used_vacation?.toFixed(1) || '0'} used of {ptoBalance?.vacation_balance?.toFixed(1) || '0'} total
          </p>
          {editingBalance && (
            <div className="mt-2">
              <label className="text-xs text-muted-foreground">Accrual (days/month)</label>
              <Input
                type="number"
                step="0.25"
                className="w-full h-8 text-sm"
                value={editedBalance.vacation_accrual_rate || 0}
                onChange={(e) => setEditedBalance({ ...editedBalance, vacation_accrual_rate: e.target.value })}
              />
            </div>
          )}
        </Card>

        <Card className="p-4 bg-orange-50 border-orange-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ThermometerSun className="text-orange-600" size={20} />
              <span className="font-medium">Sick</span>
            </div>
            {editingBalance ? (
              <Input
                type="number"
                step="0.5"
                className="w-20 h-8 text-right"
                value={editedBalance.sick_balance || 0}
                onChange={(e) => setEditedBalance({ ...editedBalance, sick_balance: e.target.value })}
              />
            ) : (
              <span className="text-2xl font-bold text-orange-600">
                {ptoBalance?.available_sick?.toFixed(1) || '0.0'}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {ptoBalance?.used_sick?.toFixed(1) || '0'} used of {ptoBalance?.sick_balance?.toFixed(1) || '0'} total
          </p>
          {editingBalance && (
            <div className="mt-2">
              <label className="text-xs text-muted-foreground">Accrual (days/month)</label>
              <Input
                type="number"
                step="0.25"
                className="w-full h-8 text-sm"
                value={editedBalance.sick_accrual_rate || 0}
                onChange={(e) => setEditedBalance({ ...editedBalance, sick_accrual_rate: e.target.value })}
              />
            </div>
          )}
        </Card>

        <Card className="p-4 bg-purple-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Coffee className="text-purple-600" size={20} />
              <span className="font-medium">Personal</span>
            </div>
            {editingBalance ? (
              <Input
                type="number"
                step="0.5"
                className="w-20 h-8 text-right"
                value={editedBalance.personal_balance || 0}
                onChange={(e) => setEditedBalance({ ...editedBalance, personal_balance: e.target.value })}
              />
            ) : (
              <span className="text-2xl font-bold text-purple-600">
                {ptoBalance?.available_personal?.toFixed(1) || '0.0'}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {ptoBalance?.used_personal?.toFixed(1) || '0'} used of {ptoBalance?.personal_balance?.toFixed(1) || '0'} total
          </p>
          {editingBalance && (
            <div className="mt-2">
              <label className="text-xs text-muted-foreground">Accrual (days/month)</label>
              <Input
                type="number"
                step="0.25"
                className="w-full h-8 text-sm"
                value={editedBalance.personal_accrual_rate || 0}
                onChange={(e) => setEditedBalance({ ...editedBalance, personal_accrual_rate: e.target.value })}
              />
            </div>
          )}
        </Card>
      </div>

      {/* Balance Edit Controls */}
      {isAdmin && (
        <div className="flex justify-end gap-2">
          {editingBalance ? (
            <>
              <Button variant="outline" size="sm" onClick={() => { setEditingBalance(false); setEditedBalance(ptoBalance); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveBalance}>
                <Save size={14} className="mr-1" /> Save Balance
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setEditingBalance(true)}>
              <Edit2 size={14} className="mr-1" /> Edit Balance
            </Button>
          )}
        </div>
      )}

      {/* Time Off Requests */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock size={18} />
            Time Off Requests
          </h3>
          <Button size="sm" onClick={() => setShowNewRequest(true)} data-testid="new-pto-request">
            <Plus size={14} className="mr-1" /> New Request
          </Button>
        </div>

        {/* New Request Form */}
        {showNewRequest && (
          <Card className="p-4 mb-4 bg-muted/50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium">Start Date</label>
                <Input
                  type="date"
                  value={newRequest.start_date}
                  onChange={(e) => setNewRequest({ ...newRequest, start_date: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium">End Date</label>
                <Input
                  type="date"
                  value={newRequest.end_date}
                  onChange={(e) => setNewRequest({ ...newRequest, end_date: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Type</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newRequest.type}
                  onChange={(e) => setNewRequest({ ...newRequest, type: e.target.value })}
                >
                  {TIME_OFF_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Days</label>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={newRequest.days_requested}
                  onChange={(e) => setNewRequest({ ...newRequest, days_requested: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs font-medium">Notes (optional)</label>
              <Input
                placeholder="Reason for time off..."
                value={newRequest.notes}
                onChange={(e) => setNewRequest({ ...newRequest, notes: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSubmitRequest}>Submit Request</Button>
              <Button size="sm" variant="outline" onClick={checkVisitsDuringPeriod} disabled={checkingVisits}>
                {checkingVisits ? 'Checking...' : 'Check Visits'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowNewRequest(false); setVisitContext(null); }}>Cancel</Button>
            </div>

            {/* Visit Context Alert */}
            {visitContext && (
              <div className={`mt-3 p-3 rounded-lg border text-sm ${visitContext.has_visits ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'}`} data-testid="visit-context-alert">
                <p className={`font-medium ${visitContext.has_visits ? 'text-amber-800' : 'text-green-800'}`}>
                  {visitContext.message}
                </p>
                {visitContext.has_visits && visitContext.visits_during_period.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {visitContext.visits_during_period.slice(0, 10).map((v, i) => (
                      <div key={i} className="text-xs text-amber-700 flex items-center gap-2">
                        <span>{v.date}</span>
                        <span className="font-medium">{v.task_name}</span>
                        {v.patient_name && <span className="text-muted-foreground">- {v.patient_name}</span>}
                        <Badge className="text-[9px] px-1 py-0 bg-amber-100 text-amber-700">{v.status}</Badge>
                      </div>
                    ))}
                    {visitContext.visits_during_period.length > 10 && (
                      <p className="text-xs text-amber-600">...and {visitContext.visits_during_period.length - 10} more</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Requests List */}
        {timeOffRequests.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">No time off requests</p>
        ) : (
          <div className="space-y-2">
            {timeOffRequests.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((req) => (
              <div key={req.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    {getTypeIcon(req.type)}
                    <span className="text-sm font-medium">{req.type}</span>
                  </div>
                  <div className="text-sm">
                    {req.start_date} → {req.end_date}
                    <span className="text-muted-foreground ml-2">({req.days_requested} days)</span>
                  </div>
                  {getStatusBadge(req.status)}
                </div>
                {isAdmin && req.status === 'Pending' && (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600 hover:bg-green-50"
                      onClick={() => handleUpdateRequest(req.id, 'Approved')}
                    >
                      <Check size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleUpdateRequest(req.id, 'Denied')}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
